import { useEffect, useRef, useCallback } from 'react'
import { MantleClient } from '@heymantle/client'
import apiClient from '@/utils/apiClient'
import { StoreData } from '@/types'

const MIN_VALID_QUOTA = 100
const STORAGE_KEY = 'compra_quota_cache'
const SYNC_COOLDOWN_MS = 60_000

interface QuotaCache {
    responsesPerSurvey: number
    totalResponses: number
    syncedAt: string
}

/**
 * Detects when the server returns a suspiciously low responsesPerSurvey (<100),
 * fetches the real value from Mantle, caches it in localStorage, and tells
 * the backend to re-sync.
 *
 * Does NOT touch monthly response limits.
 */
export const useQuotaSync = (storeData: StoreData | null | undefined) => {
    const syncingRef = useRef(false)
    const lastSyncRef = useRef(0)
    const hasSyncedForQuotaRef = useRef<number | null>(null)

    const serverQuota = storeData?.subscription?.plan?.features?.responsesPerSurvey
    const mantleToken = storeData?.store?.mantleCustomerApiToken
    const serverTotalResponses = storeData?.subscription?.usageStats?.totalResponses ?? 0

    const syncQuota = useCallback(async (
        quota: number,
        token: string,
        totalResponses: number,
    ) => {
        // 1. Fetch real data from Mantle
        const mantle = new MantleClient({
            appId: import.meta.env.VITE_MANTLE_APP_ID,
            customerApiToken: token,
        })

        const customer = await mantle.getCustomer()
        if (!customer) {
            console.warn('[QuotaSync] Could not fetch Mantle customer')
            return
        }

        // 2. Extract real quota from Mantle subscription features
        const mantleFeatures = customer.subscription?.features
        const realQuota =
            mantleFeatures?.responses_per_survey?.value ??
            mantleFeatures?.survey_responses?.value ??
            null

        if (realQuota == null || (typeof realQuota === 'number' && realQuota < MIN_VALID_QUOTA)) {
            console.warn('[QuotaSync] Mantle quota also invalid, skipping', { realQuota })
            return
        }

        // 3. Extract real usage from Mantle
        // Mantle keys usage by event name (ResponseUsage), not feature key
        const realUsage =
            customer.features?.survey_responses?.usage?.currentBillingPeriodValue ??
            customer.usage?.ResponseUsage?.currentBillingPeriodValue ??
            customer.usage?.response_usage?.currentBillingPeriodValue ??
            null

        const corrected: QuotaCache = {
            responsesPerSurvey: typeof realQuota === 'number' ? realQuota : MIN_VALID_QUOTA,
            totalResponses: typeof realUsage === 'number' ? realUsage : totalResponses,
            syncedAt: new Date().toISOString(),
        }

        // 4. Cache locally
        localStorage.setItem(STORAGE_KEY, JSON.stringify(corrected))
        console.info('[QuotaSync] Cached corrected quota', corrected)

        // 5. Tell backend to re-sync (fire-and-forget)
        apiClient
            .post('/billing/mantle/sync-quota', {
                responsesPerSurvey: corrected.responsesPerSurvey,
                totalResponses: corrected.totalResponses,
            })
            .then(() => console.info('[QuotaSync] Backend sync triggered'))
            .catch((err) => console.warn('[QuotaSync] Backend sync failed (non-critical)', err))
    }, [])

    useEffect(() => {
        // Nothing to check yet
        if (serverQuota == null || !mantleToken) return

        // Only sync when quota is exactly 0
        if (serverQuota !== 0) return

        // Already synced for this exact bad value (prevents re-firing after refetch
        // returns the same stale number before backend corrects it)
        if (hasSyncedForQuotaRef.current === serverQuota) return

        // Cooldown
        const now = Date.now()
        if (now - lastSyncRef.current < SYNC_COOLDOWN_MS) return
        if (syncingRef.current) return

        syncingRef.current = true
        lastSyncRef.current = now
        hasSyncedForQuotaRef.current = serverQuota

        syncQuota(serverQuota, mantleToken, serverTotalResponses).finally(() => {
            syncingRef.current = false
        })
    }, [serverQuota, mantleToken, serverTotalResponses, syncQuota])
}

// ── Helpers for consumers ──────────────────────────────────────────

export const getCachedQuota = (): QuotaCache | null => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        return raw ? JSON.parse(raw) : null
    } catch {
        return null
    }
}

/**
 * Returns the best-available responsesPerSurvey.
 * Uses server value when valid, falls back to localStorage cache.
 */
export const getEffectiveQuota = (
    serverQuota: number | undefined | null,
): number | null => {
    if (serverQuota != null && serverQuota !== 0) return serverQuota
    return getCachedQuota()?.responsesPerSurvey ?? serverQuota ?? null
}
